import { feedbackSectionIdToString } from 'npo/pages/NPOFeedback/utils';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import { Portfolio } from './api';
import { FeedbackQuestion } from 'npo/pages/NPOFeedback/MakeEdits/api';

export const isInitialCustomQuestion = (reportStage: AllReportStage[]) => {
  for (const s of reportStage) {
    if (s === 'All Stages' || s === 'INITIAL') {
      return true;
    }
  }
  return false;
};

export const getAllCustomQuestionSections = (
  questions: CustomSingleQuestion[],
): string => {
  if (questions.length === 0) return 'No questions are added.';
  return questions
    .map(x => feedbackSectionIdToString(x.sectionId))
    .filter((value, index, self) => self.indexOf(value) === index)
    .join(', ');
};

export interface CustomQuestionTypeProps {
  isEdit: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  sectionId: CustomQuestionSectionId;
  question: CustomSingleQuestion;
  onEditClick: () => unknown;
  handleDelete: () => unknown;
  handleSave: (answer: CustomQuestionStructure) => unknown;
}

export type CustomQuestionType =
  | 'comment'
  | 'text'
  | 'integer'
  | 'multi-text'
  | 'multi-integer'
  | 'dropdown'
  | 'guidance'
  | 'matrix-dropdown'
  | 'radio-group'
  | 'checkbox';

export const customQuestionTypes: CustomQuestionType[] = [
  'comment',
  'text',
  'integer',
  'multi-text',
  'multi-integer',
  'dropdown',
  'matrix-dropdown',
  'radio-group',
  'checkbox',
  'guidance',
];

export type GuidanceTextStructure = {
  questionId: number;
  guidanceText: string;
};
export type MultiTextType = { id?: number; placeholder: string };
export type TextIntegerQuestionType = { id?: number; label: string };
export type CustomQuestionAnswerStructure =
  | {
      questionType: Extract<CustomQuestionType, 'comment' | 'text' | 'integer'>;
      answer: string;
    }
  | {
      questionType: Extract<CustomQuestionType, 'multi-text' | 'multi-integer'>;
      answer: MultiTextType[];
    }
  | {
      questionType: Extract<CustomQuestionType, 'radio-group'>;
      answer: TextIntegerQuestionType;
    }
  | {
      questionType: Extract<CustomQuestionType, 'dropdown' | 'checkbox'>;
      answer: TextIntegerQuestionType[];
    }
  | {
      questionType: Extract<CustomQuestionType, 'matrix-dropdown'>;
      answer: undefined;
    };
export type CustomQuestionStructure =
  | {
      questionType: Extract<CustomQuestionType, 'comment' | 'text' | 'integer'>;
      structure: undefined;
    }
  | {
      questionType: Extract<CustomQuestionType, 'multi-text' | 'multi-integer'>;
      structure: MultiTextType[];
    }
  | {
      questionType: Extract<
        CustomQuestionType,
        'dropdown' | 'checkbox' | 'radio-group'
      >;
      structure: TextIntegerQuestionType[];
    }
  | {
      questionType: Extract<CustomQuestionType, 'matrix-dropdown'>;
      structure: undefined;
    }
  | {
      questionType: Extract<CustomQuestionType, 'guidance'>;
      structure: GuidanceTextStructure[];
    };

export const getCustomQuestionTypeLabel = (
  type: CustomQuestionType,
): string => {
  switch (type) {
    case 'comment':
      return 'Comment';
    case 'text':
      return 'Text';
    case 'integer':
      return 'Integer';
    case 'multi-text':
      return 'Multi text';
    case 'multi-integer':
      return 'Multi integer';
    case 'dropdown':
      return 'Dropdown';
    case 'matrix-dropdown':
      return 'Matrix dropdown';
    case 'radio-group':
      return 'Radio group';
    case 'checkbox':
      return 'Checkbox';
    case 'guidance':
      return 'Guidance text';
  }
};

export type CustomQuestionSectionId = FeedbackSectionId | 'miscellaneous';

export interface CustomQuestionAnswer {
  // questionId: number;
  dbId: number | undefined;
  optionId: number | undefined;
  value: string;
}

export interface CustomSingleQuestion {
  dbId: number | string;
  surveyId: number | string;
  questionType: CustomQuestionType;
  questionText: string;
  helperText?: string;
  sectionId: CustomQuestionSectionId;
  questionStructure: CustomQuestionStructure;
  answer: CustomQuestionAnswer[];
}

export type AllReportStage = ReportStage | 'All Stages';
export interface CustomQuestion {
  dbId: number | string;
  portfolioId: number | undefined;
  questions: CustomSingleQuestion[];
  portfolio: Portfolio | undefined;
  years: string[];
  reportStage: AllReportStage[];
  surveyName: string;
  description: string;
  enabled: boolean;
}

export const mapQuestionTypeToReport = (
  c: CustomSingleQuestion,
): FeedbackQuestion['questionType'] => {
  switch (c.questionType) {
    case 'checkbox':
    case 'dropdown':
      return 'CHECKBOXES';
    case 'text':
    case 'integer':
    case 'comment':
    case 'guidance':
    case 'multi-text':
    case 'radio-group':
    case 'multi-integer':
    case 'matrix-dropdown':
      return 'LONG_TEXT';
  }
};
